import { environment } from './../environments/environment';
import { BrowserModule } from '@angular/platform-browser';
import {
  NgModule,
  Injectable,
  ErrorHandler,
  Injector,
  APP_INITIALIZER,
  SecurityContext,
  inject,
  PLATFORM_ID
} from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';

import {
  NbThemeModule,
  NbMenuModule,
  NbSidebarModule,
  NbDatepickerModule,
  NbDialogModule,
  NbWindowModule,
  NbToastrModule,
  NbToastrService,
  NbTimepickerModule
} from '@nebular/theme';

import { SharedModule } from './shared/shared.module';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { NbAuthModule } from '@nebular/auth';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MarkdownModule } from 'ngx-markdown';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { initializeAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { providePerformance, getPerformance } from '@angular/fire/performance';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { isPlatformServer } from '@angular/common';
import { provideAppCheck, initializeAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { AuthService } from './auth/auth.service';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

declare let newrelic;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}
  async handleError(error: Error) {
    const authService = this.injector.get(AuthService);
    if (
      (error?.name !== 'FirebaseError' && error?.message?.indexOf('FirebaseError') < 0) ||
      error?.message?.indexOf('code=permission-denied') > -1
    ) {
      const toastService = this.injector.get(NbToastrService);
      const message = error.message;
      console.log(message);
      toastService.danger(message, 'We hit a glitch');
    }
    console.error('Global Error Handler', error);
    const user = await authService.getCurrentUser();
    const uid = { uid: user.uid || 'unknown' };
    newrelic.noticeError(error, { globalHandler: true, ...uid });
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    ScrollingModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    SharedModule,
    MarkdownModule.forRoot({
      sanitize: SecurityContext.NONE
    }),
    NbThemeModule.forRoot({ name: 'default' }),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbAuthModule.forRoot(),
    NbEvaIconsModule
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideFunctions(() => getFunctions()),
    provideStorage(() => getStorage()),
    provideRemoteConfig(() => {
      const r = getRemoteConfig();
      r.settings.minimumFetchIntervalMillis = environment.production ? 300000 : 5000;
      return r;
    }),
    provideAnalytics(() => {
      const anal = initializeAnalytics(getApp(), {
        config: {
          send_page_view: true,
          allow_ad_personalization_signals: true,
          anonymize_ip: true
        }
      });
      anal.app.automaticDataCollectionEnabled = true;
      return anal;
    }),
    providePerformance(() => {
      const p = getPerformance(getApp());
      p.dataCollectionEnabled = true;
      p.instrumentationEnabled = true;
      return p;
    }),
    provideAppCheck(() => {
      const platformId = inject(PLATFORM_ID);
      if (isPlatformServer(platformId)) {
        return;
      }
      const a = getApp();
      const ac = initializeAppCheck(a, {
        provider: new ReCaptchaV3Provider(environment.recaptchaSiteKeyV3),
        isTokenAutoRefreshEnabled: true
      });
      return ac;
    }),
    provideCharts(withDefaultRegisterables()),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    // The APP_INITIALIZER multi-provider allows for additional asynchronous
    // configuration to take place during the bootstrapping process. These
    // initializers will "block" the application until the returned Promises are
    // resolved, which allows us to make a request to the remote server in order to
    // gather user-specific or environment-specific configuration values that are NOT
    // available at compile-time.
    {
      provide: APP_INITIALIZER,
      useFactory: (): (() => Promise<void>) => {
        async function asyncInitializer(): Promise<void> {
          // The .loadRemoteConfig() method will trigger an AJAX request that
          // will populate the AppConfig instance. This way, it will contain
          // the remote configuration data by the time that it gets injected
          // into other Providers and Classes.
          try {
            //
          } catch (error) {
            console.log('Issue starting remote config', error);
          }
        }

        // NOTE: The factory function returns the asynchronous FUNCTION - it does
        // not execute the function directly.
        return asyncInitializer;
      },
      deps: [],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
